import { useEffect, useState, type ReactNode } from 'react';
import { Modal } from '../Modal';
import type { ContainerKind } from './types';

interface IdJsonModalProps {
  isOpen: boolean;
  container: ContainerKind;
  id: string;
  onClose: () => void;
  fetchJson: (container: ContainerKind, id: string) => Promise<unknown>;
  renderJson?: (doc: unknown, container: ContainerKind) => ReactNode;
}

export function IdJsonModal({
  isOpen,
  container,
  id,
  onClose,
  fetchJson,
  renderJson,
}: IdJsonModalProps) {
  const [doc, setDoc] = useState<unknown>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setDoc(undefined);
    setError(null);
    fetchJson(container, id)
      .then(d => { if (!cancelled) setDoc(d); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to fetch'); });
    return () => { cancelled = true; };
  }, [isOpen, container, id, fetchJson]);

  const handleCopyJson = () => {
    if (doc === undefined) return;
    navigator.clipboard.writeText(JSON.stringify(doc, null, 2));
    setCopyToast(true);
    setTimeout(() => setCopyToast(false), 1500);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${container} · ${id}`}
      size="lg"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {copyToast && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Copied</span>}
          <button className="id-control-menu-btn" onClick={handleCopyJson} disabled={doc === undefined}>
            Copy JSON
          </button>
        </div>
      }
    >
      {doc === undefined && !error && (
        <div className="id-control-modal-status">Loading...</div>
      )}
      {error && (
        <div className="id-control-modal-status id-control-modal-error">{error}</div>
      )}
      {doc !== undefined && !error && (
        <div className="id-control-json-wrap">
          {renderJson ? renderJson(doc, container) : (
            <pre className="id-control-json-fallback">{JSON.stringify(doc, null, 2)}</pre>
          )}
        </div>
      )}
    </Modal>
  );
}
